import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConversationStatus, allStatuses, getStatusConfig } from "@/types/conversation";
import { StatusBadge } from "./status-badge";
import { statusService } from "@/services/statusService";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface StatusDropdownProps {
  conversationId: string;
  currentStatus: ConversationStatus | null | undefined;
  onStatusChange?: (newStatus: ConversationStatus) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  defaultStatus?: ConversationStatus;
}

export function StatusDropdown({
  conversationId,
  currentStatus,
  onStatusChange,
  disabled = false,
  size = "md",
  className,
  defaultStatus = 'open'
}: StatusDropdownProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const handleStatusChange = async (newStatus: ConversationStatus) => {
    if (!conversationId) {
      console.error("No conversation ID provided");
      toast({
        title: "Error",
        description: "Cannot update status: No conversation ID",
        variant: "destructive",
      });
      return;
    }

    if (newStatus === currentStatus || isUpdating) return;

    setIsUpdating(true);
    
    try {
      const result = await statusService.updateConversationStatus(
        conversationId,
        newStatus
      );

      if (result.success) {
        // Call the callback if provided
        onStatusChange?.(newStatus);
        
        toast({
          title: "Status Updated",
          description: `Conversation status changed to ${getStatusConfig(newStatus).label}`,
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update status",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update conversation status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // If updating, show loading state
  if (isUpdating) {
    return (
      <div className="inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-gray-500">Updating...</span>
      </div>
    );
  }

  return (
    <Select
      value={currentStatus || defaultStatus}
      onValueChange={handleStatusChange}
      disabled={disabled}
    >
      <SelectTrigger className={`border-0 bg-transparent p-0 h-auto w-auto hover:opacity-75 transition-opacity ${className}`}>
        <StatusBadge 
          status={currentStatus} 
          size={size}
          className="cursor-pointer"
        />
      </SelectTrigger>
      <SelectContent>
        {allStatuses.map((status) => {
          const config = getStatusConfig(status);
          return (
            <SelectItem 
              key={status} 
              value={status}
              className="hover:bg-slate-50 focus:bg-slate-50 data-[highlighted]:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <StatusBadge 
                  status={status} 
                  size="sm"
                  showIcon={true}
                />
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
} 